import { useState, useEffect, useCallback } from 'react';
import { DialogTrigger, Dialog, Modal, Heading } from 'react-aria-components';
import { useForm, SubmitHandler } from 'react-hook-form';

import { useAtom } from 'jotai';

import { createChild } from '@/api/createChild';
import { getGuardians } from '@/api/getGuardians';
import { getOffices } from '@/api/getOffices';
import Button from '@/components/Button/Button';
import DateField from '@/components/DateField/DateField';
import InputText from '@/components/InputText/InputText';
import MultiSelectField from '@/components/SelectField/MultiSelectField';
import SelectField from '@/components/SelectField/SelectField';
import { UserAtom, NotificationAtom } from '@/jotai/jotai';
import { formatDate } from '@/logics/date/date';
import { Guardian } from '@/types/guardian';
import { Office } from '@/types/office';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  afterSubmit: () => void;
};

type FormData = {
  contractStartDate: Date;
  contractEndDate?: Date;
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  guardianID: number;
  guardianRelationship: 'father' | 'mother' | 'grandfather' | 'grandmother' | 'other';
  email: string;
  phoneNumber: string;
  notes?: string;
  office_ids: number[];
};

const guardianRelationshipOptions = [
  { value: 'father', label: '父' },
  { value: 'mother', label: '母' },
  { value: 'grandfather', label: '祖父' },
  { value: 'grandmother', label: '祖母' },
  { value: 'uncle', label: '叔父' },
  { value: 'aunt', label: '叔母' },
  { value: 'other', label: 'その他' },
];

export const CreateChildDialog = ({ isOpen, setIsOpen, afterSubmit }: Props) => {
  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);

  const [loginUser] = useAtom(UserAtom);
  const [_, setNotification] = useAtom(NotificationAtom);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const result = await createChild({
      contract_start_date: formatDate(data.contractStartDate),
      contract_end_date: data.contractEndDate ? formatDate(data.contractEndDate) : undefined,
      last_name: data.lastName,
      first_name: data.firstName,
      last_name_kana: data.lastNameKana,
      first_name_kana: data.firstNameKana,
      guardian_id: Number(data.guardianID), // SelectFieldのvalueはstringなのでNumberに変換
      guardian_relation_ship: data.guardianRelationship,
      email: data.email,
      phone_number: data.phoneNumber,
      notes: data.notes,
      office_ids: data.office_ids,
    });
    if (result.status === 201) {
      setNotification({
        headingLevel: 'h2',
        type: 'success',
        bannerStyle: 'standard',
        title: '利用者を登録しました',
        message: '利用者を登録しました',
      });
    } else {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '利用者の登録に失敗しました',
        message: '利用者の登録に失敗しました',
      });
    }

    setIsOpen(false);
    afterSubmit();
  };

  useEffect(() => {
    const fetchGuardians = async () => {
      const result = await getGuardians(loginUser?.email);
      if (result.status === 200) {
        setGuardians(result.result);
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '保護者の取得に失敗しました',
          message: '保護者の取得に失敗しました',
        });
      }
    };
    fetchGuardians();
    fetchOffices();
  }, [loginUser?.email]);

  const fetchOffices = useCallback(async () => {
    try {
      const result = await getOffices(loginUser?.email || '');
      if (result.status === 200) {
        setOffices(result.result);
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '事業所の取得に失敗しました',
        message: '事業所の取得に失敗しました',
      });
    }
  }, [loginUser?.email]);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Dialog className="w-[600px] rounded-lg border border-gray-400 bg-white p-6 shadow-lg outline-none">
          <Heading
            level={2}
            className="mb-6 text-xl font-bold"
          >
            利用者登録
          </Heading>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col">
              <div className="w-1/2">
                <DateField
                  label="利用契約開始日"
                  required={true}
                  errors={errors.contractStartDate}
                  control={control}
                  name="contractStartDate"
                />
              </div>
              <div className="w-1/2"></div>
              <div className="w-1/2">
                <DateField
                  label="利用契約終了日"
                  required={false}
                  errors={errors.contractEndDate}
                  control={control}
                  name="contractEndDate"
                />
              </div>
              <div className="w-1/2"></div>
            </div>
            <div className="flex flex-row gap-4">
              <InputText
                label="利用者（児童）（姓）"
                type="text"
                required={true}
                {...register('lastName')}
              />
              <InputText
                label="利用者（児童）（名）"
                type="text"
                required={true}
                {...register('firstName')}
              />
            </div>
            <div className="flex flex-row gap-4">
              <InputText
                label="利用者（児童）（セイ）"
                type="text"
                required={true}
                {...register('lastNameKana')}
              />
              <InputText
                label="利用者（児童）（メイ）"
                type="text"
                required={true}
                {...register('firstNameKana')}
              />
            </div>
            <div className="flex flex-row gap-4">
              <SelectField
                name="guardianID"
                label="保護者_1"
                control={control}
                items={guardians.map((guardian) => ({
                  label: guardian.last_name + guardian.first_name,
                  value: guardian.id.toString(),
                }))}
                required={true}
              />
              <SelectField
                name="guardianRelationship"
                label="保護者との続柄"
                control={control}
                items={guardianRelationshipOptions}
                required={true}
              />
            </div>
            <div className="flex flex-row gap-4">
              <InputText
                label="メールアドレス"
                type="text"
                required={true}
                {...register('email')}
              />
              <InputText
                label="連絡先TEL"
                type="text"
                required={true}
                {...register('phoneNumber')}
              />
            </div>
            <div className="flex flex-row gap-4">
              <InputText
                label="備考"
                type="textarea"
                required={false}
                {...register('notes')}
              />
            </div>
            <div className="flex justify-between space-x-2 p-4">
              <MultiSelectField
                name="office_ids"
                label="事業所"
                placeholder="事業所を選択"
                options={offices
                  .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
                  .map((office) => ({
                    label: office.name,
                    value: office.id,
                  }))}
                control={control}
                required
              />
            </div>
            <div className="mt-10 flex flex-row items-center justify-center space-x-2">
              <Button
                type="submit"
                size="lg"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                戻る
              </Button>
              <Button
                type="submit"
                size="lg"
                variant="solid-fill"
              >
                決定
              </Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};
